<template>
  <div>
    <StationMonitorModal v-model="stationMonitorOpen" :station="station" />
    <StationTrackNameModal v-model="stationTrackNameModalOpen" :station="station" />

    <div class="tw-w-full tw-border tw-border-white/12 tw-backdrop-blur">
      <div class="tw-flex tw-items-center tw-p-1 tw-transition-colors" :class="headerAlertBgClass">
        <div class="tw-w-0 tw-flex-1">
          <Text v-tooltip="nateVersionsTooltip" tag="p" class="tw-block tw-truncate">
            <Link v-if="nateRunnerUrl" :href="nateRunnerUrl" scheme="custom" target="_blank"
              >{{ stationLabel
              }}<template v-if="station.nateRunnerVersion"> [{{ station.nateVersion }}]</template></Link
            >
            <template v-else
              >{{ stationLabel
              }}<template v-if="station.nateRunnerVersion"> [{{ station.nateVersion }}]</template></template
            >
          </Text>
        </div>

        <div class="tw-flex">
          <template v-if="allowControl">
            <StationRecordingButtons :station="station" />

            <CircularButton
              icon="pencil"
              size="xsmall"
              color="transparentWhite"
              :disabled="!stationOk"
              @click="openStationTrackNameModal"
            />
          </template>

          <CircularButton
            v-if="stationAlerts.length > 0 || stationMutedAlerts.length > 0"
            v-tooltip="alertTooltip"
            :icon="stationAlerts.length > 0 ? BellRingOutlineSvg : BellOffOutlineSvg"
            size="xsmall"
            color="transparentWhite"
            :class="alertIconClass"
            :aria-label="$t('notifications_modal.title')"
            @click="notificationsModalStore.openModalForStation(notificationStationLabel)"
          />

          <CircularButton
            v-tooltip="audioTooltip"
            :icon="audioStreamIcon"
            size="xsmall"
            color="transparentWhite"
            :disabled="!stationOk"
            :loading="audioLoading"
            :class="audioStream && !audioLoading && 'tw-text-state-succ'"
            @click="toggleAudioStream"
          >
            <audio ref="audioRef" preload="none" />
          </CircularButton>

          <CircularButton
            :disabled="!stationOk"
            :icon="MonitorSvg"
            size="xsmall"
            color="transparentWhite"
            @click="openStationMonitor"
          />
          <CircularButton icon="close" size="xsmall" color="transparentWhite" @click="unselectStation(station.host)" />
        </div>
      </div>

      <div class="tw-h-[1px] tw-w-full tw-bg-white/12 tw-backdrop-blur"></div>

      <Link v-if="stationOk && nateUiUrl" :href="nateUiUrl" target="_blank" class="tw-block">
        <img class="tw-aspect-video tw-w-full" :src="previewUrl" />
      </Link>

      <div v-else class="tw-flex tw-aspect-video tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-bg-white/12">
        <Spinner v-if="stationLoading" />
        <div v-else class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-px-5">
          <span v-tooltip="statusMessage">
            <Icon name="warning" class="tw-size-6 tw-fill-current" :class="statusColorClass" />
          </span>
          <Text class="tw-text-center" :class="statusColorClass">{{ statusLabel }}</Text>

          <Link
            v-if="showNateRunnerLink"
            :href="nateRunnerUrl"
            scheme="underline"
            class="tw-text-white/68"
            target="_blank"
          >
            Open NATE Runner
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useEventListener } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { CircularButton, Icon, Link, Spinner, Text } from '@slideslive/fuse-kit/vue';

import BellOffOutlineSvg from '@/assets/mdi/bell-off-outline.svg';
import BellRingOutlineSvg from '@/assets/mdi/bell-ring-outline.svg';
import MonitorSvg from '@/assets/mdi/monitor.svg';
import useStation from '@/composables/use_station';
import useNotificationsModalStore from '@/stores/notifications_modal';
import useSettingsStore from '@/stores/settings';
import useStationsStore from '@/stores/stations';

import StationMonitorModal from './StationMonitorModal.vue';
import StationRecordingButtons from './StationRecordingButtons.vue';
import StationTrackNameModal from './StationTrackNameModal.vue';

const stationMonitorOpen = ref(false);
const stationTrackNameModalOpen = ref(false);

const props = defineProps({
  station: { type: Object, required: true },
});

const { t } = useI18n();
const stationsStore = useStationsStore();
const { unselectStation } = stationsStore;
const { activeAudioHost } = storeToRefs(stationsStore);
const settingsStore = useSettingsStore();
const { allowControl, tooltips, audioMonitorMix } = storeToRefs(settingsStore);

const audioRef = ref(null);
const audioStream = ref(false);
const audioLoading = ref(false);

const audioStreamIcon = computed(() => (audioStream.value ? 'volume-up' : 'volume-off'));
const audioSrc = computed(() =>
  props.station.nateApiUrl ? `${props.station.nateApiUrl}/streams/audio?mix=${audioMonitorMix.value}` : null,
);
const audioTooltip = computed(() => ({
  disabled: !tooltips.value || !audioStream.value,
  content: `Audio monitor mix: ${audioMonitorMix.value}`,
}));

const playAudioStream = async () => {
  if (!audioSrc.value) return;

  audioRef.value.src = audioSrc.value;
  audioRef.value.load();

  try {
    await audioRef.value.play();
  } catch {
    audioRef.value.src = '';
    audioStream.value = false;
    audioLoading.value = false;
    activeAudioHost.value = null;
  }
};

const stopAudioStream = () => {
  audioRef.value.pause();
  audioRef.value.src = '';
  audioLoading.value = false;
};

const toggleAudioStream = async () => {
  if (audioStream.value) {
    audioStream.value = false;
    activeAudioHost.value = null;
    stopAudioStream();
  } else {
    audioStream.value = true;
    activeAudioHost.value = props.station.host;
    await playAudioStream();
  }
};

watch(activeAudioHost, (host) => {
  if (host !== props.station.host && audioStream.value) {
    audioStream.value = false;
    stopAudioStream();
  }
});

watch(audioMonitorMix, async () => {
  if (!audioStream.value) return;

  stopAudioStream();
  await playAudioStream();
});

useEventListener(audioRef, 'loadstart', () => {
  if (!audioStream.value) return;

  audioLoading.value = true;
});

useEventListener(audioRef, 'canplay', () => {
  audioLoading.value = false;
});

const { previewUrl, stationLabel, statusLabel, statusMessage, statusColorClass } = useStation({
  station: () => props.station,
});

const notificationsModalStore = useNotificationsModalStore();
const { newMessages, mutedMessages } = storeToRefs(notificationsModalStore);

const notificationStationLabel = computed(() =>
  [props.station.stationName, props.station.trackName].filter(Boolean).join(': '),
);

const stationAlerts = computed(() =>
  newMessages.value.filter((m) => m.station_label === notificationStationLabel.value),
);

const stationMutedAlerts = computed(() =>
  mutedMessages.value.filter((m) => m.station_label === notificationStationLabel.value),
);

const highestSeverityOf = (msgs) => {
  if (msgs.some((m) => m.level === 'error')) return 'error';
  if (msgs.some((m) => m.level === 'warning')) return 'warning';
  if (msgs.length) return 'info';
  return null;
};

const stationHighestSeverity = computed(() => highestSeverityOf(stationAlerts.value));
const stationMutedHighestSeverity = computed(() => highestSeverityOf(stationMutedAlerts.value));
const stationEffectiveSeverity = computed(() => stationHighestSeverity.value ?? stationMutedHighestSeverity.value);

const headerAlertBgClass = computed(() => ({
  'tw-bg-state-err/25': stationEffectiveSeverity.value === 'error',
  'tw-bg-state-warn/25': stationEffectiveSeverity.value === 'warning',
  'tw-bg-state-info/25': stationEffectiveSeverity.value === 'info',
  'tw-animate-pulse': !!stationHighestSeverity.value,
}));

const alertIconClass = computed(() => ({
  'tw-text-state-err': stationEffectiveSeverity.value === 'error',
  'tw-text-state-warn': stationEffectiveSeverity.value === 'warning',
  'tw-text-state-info': stationEffectiveSeverity.value === 'info',
  'tw-opacity-50': !stationHighestSeverity.value && !!stationMutedHighestSeverity.value,
}));

const ALERT_LEVEL_COLOR = { error: '#f87171', warning: '#fb923c', info: '#60a5fa' };

const alertTooltip = computed(() => {
  const all = [
    ...stationAlerts.value.map((m) => ({ ...m, muted: false })),
    ...stationMutedAlerts.value.map((m) => ({ ...m, muted: true })),
  ];
  if (!all.length) return { disabled: true };
  const hr = '<hr style="margin: 6px 0; border-color: rgba(255,255,255,0.2)">';
  const allMuted = stationAlerts.value.length === 0;
  const header = allMuted
    ? `<div style="opacity:0.5;margin-bottom:6px">All notifications muted</div>`
    : '';
  return {
    html: true,
    content:
      header +
      all
        .map((m) => {
          const color = ALERT_LEVEL_COLOR[m.level] ?? '#fff';
          const mutedLabel = m.muted ? ' <span style="opacity:0.5;font-size:0.75em">(muted)</span>' : '';
          return `<div><strong style="color:${color}">${m.level}</strong>${mutedLabel}: ${m.title}<br>${m.text}</div>`;
        })
        .join(hr),
  };
});

const stationOk = computed(() => props.station?.status === 'ok');
const stationLoading = computed(() => props.station?.status === 'loading');
const nateUiUrl = computed(() => props.station?.nateRunnerState?.data?.nate?.ui_url ?? null);

const nateRunnerUrl = computed(() => (props.station?.host ? `http://${props.station.host}` : null));

const showNateRunnerLink = computed(() => !!nateRunnerUrl.value && props.station?.online === true);

function openStationMonitor() {
  stationMonitorOpen.value = true;
}

function openStationTrackNameModal() {
  stationTrackNameModalOpen.value = true;
}

const nateVersionsTooltip = computed(() => ({
  disabled: !tooltips.value,
  html: true,
  content: `
    <div>
      <p>${t('station_tooltip.nate_version')}: ${props.station?.nateVersion ?? t('station_tooltip.unknown')}</p>
      <p>${t('station_tooltip.nate_runner_version')}: ${props.station?.nateRunnerVersion ?? t('station_tooltip.unknown')}</p>
    </div>
  `,
}));
</script>
